import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import Navbar from "../Components/Navbar/navbar";
import {
  CONFIGURACOES_EXPORTACAO,
  exportarListasExcel,
} from "../Components/ExportarLista/ExportarLista";

export default function ExportacaoDados() {
  const [opcoesSelecionadas, setOpcoesSelecionadas] = useState([]);
  const [exportando, setExportando] = useState(false);
  const [mensagem, setMensagem] = useState(null);

  const opcoes = useMemo(() => {
    const assinaturas = new Set();

    return Object.entries(CONFIGURACOES_EXPORTACAO)
      .filter(([, configuracao]) => {
        const assinatura = configuracao.colecoes.join("|");
        if (assinaturas.has(assinatura)) return false;
        assinaturas.add(assinatura);
        return true;
      })
      .sort(([, a], [, b]) => a.titulo.localeCompare(b.titulo, "pt-BR"));
  }, []);

  const todasSelecionadas = opcoes.length > 0 && opcoesSelecionadas.length === opcoes.length;

  function alternarTodas(event) {
    setOpcoesSelecionadas(event.target.checked ? opcoes.map(([rota]) => rota) : []);
    setMensagem(null);
  }

  function alternarOpcao(rota) {
    setOpcoesSelecionadas((atuais) =>
      atuais.includes(rota)
        ? atuais.filter((item) => item !== rota)
        : [...atuais, rota]
    );
    setMensagem(null);
  }

  async function exportar() {
    const configuracoes = opcoesSelecionadas
      .map((rota) => CONFIGURACOES_EXPORTACAO[rota])
      .filter(Boolean);
    if (!configuracoes.length) {
      setMensagem({ tipo: "warning", texto: "Marque ao menos um módulo para exportar." });
      return;
    }

    try {
      setExportando(true);
      setMensagem(null);
      const resultado = await exportarListasExcel(configuracoes);
      if (resultado.falhas.length) {
        setMensagem({
          tipo: "warning",
          texto: `Planilha gerada com ${resultado.exportados} módulo(s). Sem permissão para: ${resultado.falhas.map((item) => item.titulo).join(", ")}.`,
        });
      } else {
        setMensagem({ tipo: "success", texto: "Planilha gerada com sucesso." });
      }
    } catch (error) {
      console.error("Erro ao exportar dados para consulta:", error);
      setMensagem({
        tipo: "error",
        texto: error?.message || "Não foi possível gerar a planilha.",
      });
    } finally {
      setExportando(false);
    }
  }

  return (
    <>
      <Navbar />
      <Box sx={{ maxWidth: 820, mx: "auto", px: 2, py: 5 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Exportação de dados para consulta
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Marque módulos específicos ou selecione tudo. Cada módulo será colocado em uma aba do mesmo arquivo Excel.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Esta planilha é destinada à consulta e conferência. Ela não substitui o backup restaurável do Firebase e não inclui fotos ou outros arquivos do Storage.
        </Alert>

        {mensagem && (
          <Alert severity={mensagem.tipo} sx={{ mb: 3 }} onClose={() => setMensagem(null)}>
            {mensagem.texto}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
          <Stack spacing={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={todasSelecionadas}
                  indeterminate={opcoesSelecionadas.length > 0 && !todasSelecionadas}
                  onChange={alternarTodas}
                  disabled={exportando}
                />
              }
              label={<Typography fontWeight={700}>Selecionar tudo</Typography>}
            />

            <Divider />

            <FormGroup sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 0.5 }}>
              {opcoes.map(([rota, configuracao]) => (
                <FormControlLabel
                  key={rota}
                  control={
                    <Checkbox
                      checked={opcoesSelecionadas.includes(rota)}
                      onChange={() => alternarOpcao(rota)}
                      disabled={exportando}
                    />
                  }
                  label={configuracao.titulo}
                />
              ))}
            </FormGroup>

            <Button
              variant="contained"
              startIcon={exportando ? <CircularProgress size={18} color="inherit" /> : <GridOnOutlinedIcon />}
              onClick={exportar}
              disabled={exportando || !opcoesSelecionadas.length}
              sx={{ alignSelf: "flex-start" }}
            >
              {exportando
                ? "Gerando planilha..."
                : `Baixar Excel (${opcoesSelecionadas.length} módulo${opcoesSelecionadas.length === 1 ? "" : "s"})`}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}
